import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Target,
  Scale,
  TrendingUp,
  TrendingDown,
  Plus,
  Calendar,
  Crown,
  Lock
} from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const AIGoals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [goals, setGoals] = useState(null);
  const [progress, setProgress] = useState([]);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showEntryForm, setShowEntryForm] = useState(false);
  
  const [goalForm, setGoalForm] = useState({
    current_weight: '',
    target_weight: '',
    height: '',
    age: '',
    gender: 'female',
    fat_percentage: '',
    muscle_percentage: '',
    goal_type: 'maintain'
  });

  const [entryForm, setEntryForm] = useState({
    weight: '',
    fat_percentage: '',
    muscle_percentage: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [statusRes, goalsRes, progressRes] = await Promise.all([
        axios.get(`${API}/ai/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ai/goals`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { has_goals: false } })),
        axios.get(`${API}/ai/goals/progress`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { entries: [], goals: null } }))
      ]);

      setStatus(statusRes.data);
      setGoals(goalsRes.data);
      setProgress(progressRes.data.entries || []);
      
      if (goalsRes.data.has_goals && goalsRes.data.goals) {
        setGoalForm({
          current_weight: goalsRes.data.goals.current_weight?.toString() || '',
          target_weight: goalsRes.data.goals.target_weight?.toString() || '',
          height: goalsRes.data.goals.height?.toString() || '',
          age: goalsRes.data.goals.age?.toString() || '',
          gender: goalsRes.data.goals.gender || 'female',
          fat_percentage: goalsRes.data.goals.fat_percentage?.toString() || '',
          muscle_percentage: goalsRes.data.goals.muscle_percentage?.toString() || '',
          goal_type: goalsRes.data.goals.goal_type || 'maintain'
        });
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoals = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/ai/goals`,
        {
          current_weight: parseFloat(goalForm.current_weight),
          target_weight: parseFloat(goalForm.target_weight),
          height: parseFloat(goalForm.height),
          age: parseInt(goalForm.age),
          gender: goalForm.gender,
          fat_percentage: goalForm.fat_percentage ? parseFloat(goalForm.fat_percentage) : null,
          muscle_percentage: goalForm.muscle_percentage ? parseFloat(goalForm.muscle_percentage) : null,
          goal_type: goalForm.goal_type
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Hedefler kaydedildi!');
      setShowGoalForm(false);
      fetchData();
    } catch (error) {
      toast.error('Hedefler kaydedilemedi');
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/ai/goals/entry`,
        {
          weight: parseFloat(entryForm.weight),
          fat_percentage: entryForm.fat_percentage ? parseFloat(entryForm.fat_percentage) : null,
          muscle_percentage: entryForm.muscle_percentage ? parseFloat(entryForm.muscle_percentage) : null,
          notes: entryForm.notes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('İlerleme kaydedildi!');
      setShowEntryForm(false);
      setEntryForm({ weight: '', fat_percentage: '', muscle_percentage: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('İlerleme kaydedilemedi');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Premium check
  if (!status?.is_premium) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 via-white to-purple-50">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate('/ai-dietitian')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AI Diyetisyen'e Dön
          </Button>
          
          <Card className="text-center py-12">
            <CardContent>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="h-10 w-10 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Premium Özellik</h2>
              <p className="text-gray-600 mb-6">
                Hedef takibi özelliği sadece Premium üyeler içindir.
                Premium'a geçerek kilo, yağ oranı ve kas gelişiminizi takip edebilirsiniz.
              </p>
              <Button
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                onClick={() => navigate('/premium')}
              >
                <Crown className="h-5 w-5 mr-2" />
                Premium'a Yükselt
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const weightDiff = goals?.has_goals ? (goals.goals.current_weight - goals.goals.target_weight).toFixed(1) : 0;
  const isLosingWeight = goals?.goals?.goal_type === 'lose';

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/ai-dietitian')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          AI Diyetisyen'e Dön
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <Target className="h-8 w-8 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Hedef Takibi</h1>
          <p className="text-gray-600">Kilo ve vücut ölçülerinizi takip edin</p>
        </div>

        {!goals?.has_goals || showGoalForm ? (
          // Goal Setup Form
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle>Hedeflerinizi Belirleyin</CardTitle>
              <CardDescription>Başlangıç değerlerinizi ve hedeflerinizi girin</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveGoals} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Mevcut Kilo (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={goalForm.current_weight}
                      onChange={(e) => setGoalForm({ ...goalForm, current_weight: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Hedef Kilo (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={goalForm.target_weight}
                      onChange={(e) => setGoalForm({ ...goalForm, target_weight: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Boy (cm)</Label>
                    <Input
                      type="number"
                      value={goalForm.height}
                      onChange={(e) => setGoalForm({ ...goalForm, height: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Yaş</Label>
                    <Input
                      type="number"
                      value={goalForm.age}
                      onChange={(e) => setGoalForm({ ...goalForm, age: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Cinsiyet</Label>
                    <Select
                      value={goalForm.gender}
                      onValueChange={(value) => setGoalForm({ ...goalForm, gender: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="female">Kadın</SelectItem>
                        <SelectItem value="male">Erkek</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Yağ Oranı % (opsiyonel)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={goalForm.fat_percentage}
                      onChange={(e) => setGoalForm({ ...goalForm, fat_percentage: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Kas Oranı % (opsiyonel)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={goalForm.muscle_percentage}
                      onChange={(e) => setGoalForm({ ...goalForm, muscle_percentage: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Hedefiniz</Label>
                  <Select
                    value={goalForm.goal_type}
                    onValueChange={(value) => setGoalForm({ ...goalForm, goal_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lose">Kilo Vermek</SelectItem>
                      <SelectItem value="maintain">Korumak</SelectItem>
                      <SelectItem value="gain">Kilo/Kas Almak</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3">
                  {goals?.has_goals && (
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setShowGoalForm(false)}>
                      İptal
                    </Button>
                  )}
                  <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700">
                    Hedefleri Kaydet
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          // Goals Dashboard
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-4 text-center">
                  <Scale className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-blue-600">Mevcut Kilo</p>
                  <p className="text-3xl font-bold text-blue-700">{goals.goals?.current_weight}</p>
                  <p className="text-sm text-blue-500">kg</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4 text-center">
                  <Target className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-green-600">Hedef Kilo</p>
                  <p className="text-3xl font-bold text-green-700">{goals.goals?.target_weight}</p>
                  <p className="text-sm text-green-500">kg</p>
                </CardContent>
              </Card>

              <Card className={`bg-gradient-to-br ${parseFloat(weightDiff) > 0 ? 'from-orange-50 to-orange-100 border-orange-200' : 'from-emerald-50 to-emerald-100 border-emerald-200'}`}>
                <CardContent className="p-4 text-center">
                  {parseFloat(weightDiff) > 0 ? (
                    <TrendingDown className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                  ) : (
                    <TrendingUp className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                  )}
                  <p className={`text-sm ${parseFloat(weightDiff) > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>Kalan</p>
                  <p className={`text-3xl font-bold ${parseFloat(weightDiff) > 0 ? 'text-orange-700' : 'text-emerald-700'}`}>
                    {Math.abs(parseFloat(weightDiff))}
                  </p>
                  <p className={`text-sm ${parseFloat(weightDiff) > 0 ? 'text-orange-500' : 'text-emerald-500'}`}>kg</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 text-center">
                  <Calendar className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Kayıt Sayısı</p>
                  <p className="text-3xl font-bold text-purple-700">{progress.length}</p>
                  <p className="text-sm text-gray-500">giriş</p>
                </CardContent>
              </Card>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button onClick={() => setShowGoalForm(true)} variant="outline">
                Hedefleri Düzenle
              </Button>
              <Button onClick={() => setShowEntryForm(true)} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="h-4 w-4 mr-2" />
                İlerleme Ekle
              </Button>
            </div>

            {/* Entry Form Modal */}
            {showEntryForm && (
              <Card className="border-purple-200">
                <CardHeader>
                  <CardTitle>Yeni İlerleme Kaydet</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddEntry} className="space-y-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Kilo (kg)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={entryForm.weight}
                          onChange={(e) => setEntryForm({ ...entryForm, weight: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Yağ Oranı % (opsiyonel)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={entryForm.fat_percentage}
                          onChange={(e) => setEntryForm({ ...entryForm, fat_percentage: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Kas Oranı % (opsiyonel)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={entryForm.muscle_percentage}
                          onChange={(e) => setEntryForm({ ...entryForm, muscle_percentage: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Notlar (opsiyonel)</Label>
                      <Input
                        value={entryForm.notes}
                        onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })}
                        placeholder="Bugün nasıl hissediyorsunuz?"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowEntryForm(false)}>
                        İptal
                      </Button>
                      <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                        Kaydet
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Progress History */}
            <Card>
              <CardHeader>
                <CardTitle>İlerleme Geçmişi</CardTitle>
              </CardHeader>
              <CardContent>
                {progress.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Henüz ilerleme kaydı yok</p>
                ) : (
                  <div className="space-y-3">
                    {progress.map((entry, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-purple-700">{entry.weight}</p>
                            <p className="text-xs text-gray-500">kg</p>
                          </div>
                          {entry.fat_percentage && (
                            <Badge variant="outline">
                              Yağ: %{entry.fat_percentage}
                            </Badge>
                          )}
                          {entry.muscle_percentage && (
                            <Badge variant="outline">
                              Kas: %{entry.muscle_percentage}
                            </Badge>
                          )}
                          {entry.notes && (
                            <span className="text-sm text-gray-600">{entry.notes}</span>
                          )}
                        </div>
                        <span className="text-sm text-gray-400">
                          {new Date(entry.recorded_at).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIGoals;
